import { Dialog } from 'primereact/dialog';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './ChangelogOverlay.scss';

interface ChangelogEntry {
    message: string;
    hash: string;
    date: string;
}

interface VersionChanges {
    features: ChangelogEntry[];
    fixes: ChangelogEntry[];
    improvements: ChangelogEntry[];
    other: ChangelogEntry[];
}

interface VersionInfo {
    version: string;
    date: string;
    changes: VersionChanges;
}

interface ChangelogData {
    lastUpdated: string;
    versions: VersionInfo[];
}

interface ChangelogOverlayProps {
    visible: boolean;
    onHide: () => void;
    currentVersion: string;
    forceShow?: boolean; // When true, always show regardless of user settings
}

const STORAGE_KEY = 'changelog_dismissed_version';

export const ChangelogOverlay: React.FC<ChangelogOverlayProps> = ({
    visible,
    onHide,
    currentVersion,
    forceShow = false
}) => {
    const { t } = useTranslation();
    const [changelog, setChangelog] = useState<ChangelogData | null>(null);
    const [loading, setLoading] = useState(true);
    const [dontShowAgain, setDontShowAgain] = useState(false);

    useEffect(() => {
        if (visible) {
            loadChangelog();
        }
    }, [visible]);

    const loadChangelog = async () => {
        try {
            setLoading(true);
            const response = await fetch('/changelog.json');
            if (response.ok) {
                const data: ChangelogData = await response.json();
                setChangelog(data);
            }
        } catch (error) {
            console.error('Failed to load changelog:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (dontShowAgain && !forceShow) {
            // Only save "don't show again" preference when not manually opened
            localStorage.setItem(STORAGE_KEY, currentVersion);
        }
        onHide();
    };

    const formatCommitMessage = (message: string): string => {
        // Clean up commit message - only remove conventional commit prefixes, not ticket numbers
        let cleanedMessage = message
            // Remove conventional commit prefixes like "feat:", "fix:", etc.
            .replace(/^(feat|fix|docs|style|refactor|test|chore)(\([^)]*\))?:\s*/i, '')
            // Remove single word prefixes followed by colon (but preserve ticket numbers like DRGPT-46)
            .replace(/^(?!.*-\d+)[a-zA-Z]+:\s*/, '');
        
        // Capitalize first letter
        return cleanedMessage.charAt(0).toUpperCase() + cleanedMessage.slice(1);
    };

    const renderChangeSection = (title: string, changes: ChangelogEntry[], icon: string) => {
        if (changes.length === 0) return null;

        return (
            <div className="change-section">
                <h4 className="change-section-title">
                    <i className={`pi ${icon}`} />
                    {title}
                </h4>
                <ul className="change-list">
                    {changes.map((change, index) => (
                        <li key={index} className="change-item">
                            {formatCommitMessage(change.message)}
                        </li>
                    ))}
                </ul>
            </div>
        );
    };

    const renderVersion = (version: VersionInfo, isLatest: boolean = false) => (
        <div key={version.version} className={`version-section ${isLatest ? 'latest-version' : ''}`}>
            <div className="version-header">
                <h3 className="version-title">
                    {t('changelog.version')} {version.version}
                    {isLatest && <span className="latest-badge">{t('changelog.latest_badge')}</span>}
                </h3>
                <span className="version-date">{version.date}</span>
            </div>
            
            <div className="version-changes">
                {renderChangeSection(t('changelog.sections.features'), version.changes.features, 'pi-star')}
                {renderChangeSection(t('changelog.sections.fixes'), version.changes.fixes, 'pi-wrench')}
                {renderChangeSection(t('changelog.sections.improvements'), version.changes.improvements, 'pi-arrow-up')}
                {renderChangeSection(t('changelog.sections.other'), version.changes.other, 'pi-file')}
            </div>
        </div>
    );

    if (loading) {
        return (
            <Dialog
                visible={visible}
                onHide={onHide}
                header={t('changelog.title')}
                className="changelog-overlay loading"
                style={{ width: '600px', maxHeight: '80vh' }}
                draggable={false}
            >
                <div className="loading-content">
                    <i className="pi pi-spin pi-spinner" style={{ fontSize: '2em' }} />
                    <p>{t('changelog.loading')}</p>
                </div>
            </Dialog>
        );
    }

    if (!changelog || changelog.versions.length === 0) {
        return null;
    }

    return (
        <Dialog
            visible={visible}
            onHide={handleClose}
            header={
                <div className="changelog-header">
                    <div>
                        <h2>{t('changelog.title')}</h2>
                        <p>{t('changelog.subtitle')}</p>
                    </div>
                </div>
            }
            className="changelog-overlay"
            style={{ width: '700px', maxHeight: '80vh' }}
            draggable={false}
            footer={
                <div className="changelog-footer">
                    {!forceShow && (
                        <div className="dont-show-checkbox">
                            <Checkbox
                                inputId="dont-show-again"
                                checked={dontShowAgain}
                                onChange={(e) => setDontShowAgain(e.checked)}
                            />
                            <label htmlFor="dont-show-again">
                                {t('changelog.dont_show_again')}
                            </label>
                        </div>
                    )}
                    <Button
                        label={t('globals.actions.close')}
                        onClick={handleClose}
                        className="p-button-primary"
                    />
                </div>
            }
        >
            <div className="changelog-content">
                {changelog.versions.map((version, index) => 
                    renderVersion(version, index === 0)
                )}
                
                {changelog.versions.length === 0 && (
                    <div className="no-changes">
                        <i className="pi pi-info-circle" />
                        <p>{t('changelog.no_changes')}</p>
                    </div>
                )}
            </div>
        </Dialog>
    );
};

export default ChangelogOverlay;